import { saveAuditLog } from "./auditLogRepository";
import { buildAuditEntry, getChangeSnapshot } from "./auditLogService";
import { saveInvoice } from "../invoice/saveInvoice";
import { addItem } from "../itemsAndInventory/addItem";
import { getItemById } from "../itemsAndInventory/getItemById";
import { updateItem } from "../itemsAndInventory/updateItem";
import { deleteItem } from "../itemsAndInventory/deleteItem";
import { saveReturnItem } from "../returns/saveReturnItem";
import { ReturnItemData } from "../../models/credit";
import { ItemInfo } from "../../models/item";

const buildInvoiceSoldItemChanges = async (
	items: any[]
): Promise<Record<string, any> | undefined> => {
	if (!items || items.length === 0) {
		return undefined;
	}

	const changes: Record<string, any> = {};

	for (const item of items) {
		const inventoryItem = await getItemById(item.itemKey);
		const beforeAvailable = inventoryItem?.availableQuantity ?? 0;
		const afterAvailable = Math.max(
			0,
			beforeAvailable - (item.quantity ?? 0)
		);
		const key = item.itemCode
			? `soldItem:${item.itemCode}`
			: item.itemKey
				? `soldItem:${item.itemKey}`
				: `soldItem:${item.lineItemId ?? Math.random().toString(36).slice(2)}`;

		changes[key] = {
			before: {
				itemCode: item.itemCode,
				itemKey: item.itemKey,
				description: item.description,
				availableQuantity: beforeAvailable
			},
			after: {
				itemCode: item.itemCode,
				itemKey: item.itemKey,
				description: item.description,
				availableQuantity: afterAvailable
			}
		};
	}

	return Object.keys(changes).length > 0 ? changes : undefined;
};

const normalizeChangeValue = (value: unknown): unknown =>
	value === undefined ? null : value;

const buildItemCreateChanges = (item: any): Record<string, any> | undefined => {
	if (!item) {
		return undefined;
	}

	const changed: Record<string, any> = {};
	Object.entries(item).forEach(([key, value]) => {
		changed[key] = {
			before: normalizeChangeValue(null),
			after: normalizeChangeValue(value)
		};
	});

	return Object.keys(changed).length > 0 ? changed : undefined;
};

const buildItemUpdateChanges = async (
	itemId: string,
	updatedItem: any
): Promise<Record<string, any> | undefined> => {
	const beforeItem = await getItemById(itemId);
	if (!beforeItem || !updatedItem) {
		return undefined;
	}

	return getChangeSnapshot(
		beforeItem as Record<string, unknown>,
		updatedItem as Record<string, unknown>
	) as Record<string, any> | undefined;
};

const buildItemDeleteChanges = async (
	itemId: string
): Promise<Record<string, any> | undefined> => {
	const beforeItem = await getItemById(itemId);
	if (!beforeItem) {
		return undefined;
	}

	const changed: Record<string, any> = {};
	Object.entries(beforeItem).forEach(([key, value]) => {
		changed[key] = {
			before: normalizeChangeValue(value),
			after: normalizeChangeValue(null)
		};
	});

	return Object.keys(changed).length > 0 ? changed : undefined;
};

export const auditedSaveInvoice = async (invoice: any) => {
	const invoiceId = await saveInvoice(invoice);
	try {
		const soldItemChanges = await buildInvoiceSoldItemChanges(
			invoice.items
		);
		const entry = await buildAuditEntry({
			entityType: "Invoice",
			entityId: invoiceId ?? "",
			operation: "CREATE",
			after: invoice,
			changes: soldItemChanges,
			metadata: {
				invoiceNumber: invoice.invoiceNumber,
				soldItems: invoice.items?.map((item: any) => ({
					itemCode: item.itemCode,
					itemKey: item.itemKey,
					description: item.description,
					quantity: item.quantity
				}))
			}
		});
		await saveAuditLog(entry);
	} catch (err) {
		// Log audit errors but don't block invoice persistence
		// This prevents audit log failures from stopping payments or subsequent operations
		// Caller can still check database to verify audit entries.
		// eslint-disable-next-line no-console
		console.error("Failed to create/save audit log for invoice:", err);
	}
	return invoiceId;
};

export const auditedSaveReturnItem = async (
	returnData: ReturnItemData,
	inventoryItem: ItemInfo
) => {
	const itemCode = inventoryItem?.itemCode ?? "UNKNOWN";
	const beforeAvailable = inventoryItem?.availableQuantity ?? 0;
	const afterAvailable = beforeAvailable + (returnData.quantity ?? 0);

	// Save the return item to database
	await saveReturnItem(returnData);

	try {
		// Build the audit entry with returned item quantity changes
		const returnItemKey = `returnedItem:${itemCode}`;
		const changes: Record<string, any> = {
			[returnItemKey]: {
				before: {
					itemCode,
					description: returnData.description,
					availableQuantity: beforeAvailable
				},
				after: {
					itemCode,
					description: returnData.description,
					availableQuantity: afterAvailable
				}
			}
		};

		const entry = await buildAuditEntry({
			entityType: "Return",
			entityId: returnData.lineItemId,
			operation: "CREATE",
			after: returnData as unknown as Record<string, unknown>,
			changes,
			metadata: {
				invoiceNumber: returnData.invoiceNumber,
				itemCode,
				returnedItems: [
					{
						itemCode,
						quantity: returnData.quantity ?? 0
					}
				]
			}
		});
		await saveAuditLog(entry);
	} catch (err) {
		// Log audit errors but don't block return persistence
		// eslint-disable-next-line no-console
		console.error("Failed to create/save audit log for return item:", err);
	}
};

//#region Item Operations
export const auditedAddItem = async (item: any) => {
	const itemId = await addItem(item);
	const changes = buildItemCreateChanges(item);
	const entry = await buildAuditEntry({
		entityType: "Item",
		entityId: itemId ?? "",
		operation: "CREATE",
		after: item,
		changes,
		metadata: {
			itemCode: item.itemCode
		}
	});
	await saveAuditLog(entry);
	return itemId;
};

export const auditedUpdateItem = async (itemId: string, updatedItem: any) => {
	const changes = await buildItemUpdateChanges(itemId, updatedItem);
	const entry = await buildAuditEntry({
		entityType: "Item",
		entityId: itemId,
		operation: "UPDATE",
		after: updatedItem,
		changes,
		metadata: {
			itemCode: updatedItem.itemCode
		}
	});
	await updateItem(itemId, updatedItem);
	await saveAuditLog(entry);
};

export const auditedDeleteItem = async (itemId: string) => {
	const changes = await buildItemDeleteChanges(itemId);
	const entry = await buildAuditEntry({
		entityType: "Item",
		entityId: itemId,
		operation: "DELETE",
		after: { isDeleted: true },
		changes,
		metadata: {}
	});
	await deleteItem(itemId);
	await saveAuditLog(entry);
};
//#endregion Item Operations
